import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { orderChangesApi } from "@/features/orders/api/orderChanges";

export function useOrderChangeRequests(params?: { order?: string }) {
  return useQuery({
    queryKey: ["order-change-requests", params],
    queryFn: () => orderChangesApi.list(params ? Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined) as [string, string][]) : undefined),
  });
}

export function useCreateOrderChangeRequest(orderId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: orderChangesApi.create,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["order-change-requests"] });
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["orders-pending-change-requests-count"] });
    },
  });
}

export function useApproveOrderChangeRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      comment,
      line_item_overrides,
      wpw_owner_changes,
    }: {
      id: string;
      comment?: string;
      line_item_overrides?: import("@/features/orders/api/orderChanges").LineItemOverride[];
      wpw_owner_changes?: import("@/features/orders/types").WpwOwnerChangeInput[];
    }) => orderChangesApi.approve(id, comment, line_item_overrides, wpw_owner_changes),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["order-change-requests"] });
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["orders-pending-change-requests-count"] });
      // The WPW list may have changed with the approval.
      qc.invalidateQueries({ queryKey: ["order-wpw-scope"] });
    },
  });
}

export function useRejectOrderChangeRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, comment }: { id: string; comment?: string }) => orderChangesApi.reject(id, comment),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["order-change-requests"] });
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["orders-pending-change-requests-count"] });
    },
  });
}

export function useWithdrawOrderChangeRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => orderChangesApi.withdraw(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["order-change-requests"] });
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["orders-pending-change-requests-count"] });
    },
  });
}

// Lista WPW zamówienia dla formularza wniosku o zmianę (GK, zamówienia WPW).
export function useOrderWpwScope(orderId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["order-wpw-scope", orderId],
    queryFn: () => orderChangesApi.wpwScope(orderId),
    enabled,
  });
}
